import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InputMask from "react-input-mask";
import Swal from "sweetalert2";
import api from "../../services/api";
import { usePluginspace } from "../../hooks/usePluginspace";
import {
  MapPin,
  CalendarDays,
  Clock,
  Users,
  Sparkles,
  SearchX,
  Accessibility,
} from "lucide-react";
import "./index.css";

// Adicionar estilos de animação
const styleElement = document.createElement("style");
styleElement.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
if (
  !document.head.querySelector('style[data-component="reservation-search"]')
) {
  styleElement.setAttribute("data-component", "reservation-search");
  document.head.appendChild(styleElement);
}

interface Booking {
  id: string;
  name: string;
  email: string;
  phone: string;
  date: string;
  hour: {
    id: string;
    hour: string;
  };
  people: number;
  bookingEnvironment: {
    id: string;
    name: string;
  };
  finalized: string;
  customerArrived: number;
}

// Função para formatar data sem problemas de timezone (mesmo padrão do Admin)
const formatDateBR = (dateString: string): string => {
  try {
    // Extrai apenas a parte da data (YYYY-MM-DD) ignorando timezone
    const datePart = dateString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
};

// Função para verificar se a data da reserva é atual ou futura
const isCurrentOrFutureDate = (dateString: string): boolean => {
  try {
    // Extrai apenas a parte da data (YYYY-MM-DD) ignorando timezone
    const datePart = dateString.split("T")[0];
    const bookingDate = new Date(datePart);
    
    // Obtém a data de hoje em YYYY-MM-DD
    const today = new Date();
    const todayString = today.toISOString().split("T")[0];
    const todayDate = new Date(todayString);
    
    // Retorna true se a data da reserva >= hoje
    return bookingDate >= todayDate;
  } catch {
    // Se houver erro ao fazer parse, considera como válida
    return true;
  }
};

// Função para obter informações completas do status da reserva
interface BookingStatusInfo {
  label: string;
  background: string;
  color: string;
  border: string;
}

const getBookingStatusInfo = (
  finalized: string | null,
  customerArrived: number
): BookingStatusInfo => {
  // Prioridade: canceled > noShow > null > customerArrived > confirmed
  if (finalized === "canceled") {
    return {
      label: "Cancelada",
      background: "rgba(239, 83, 80, 0.15)",
      color: "#5f0606",
      border: "rgba(239, 83, 80, 0.3)",
    };
  }

  if (finalized === "noShow") {
    return {
      label: "Não Compareceu",
      background: "rgba(255, 152, 0, 0.15)",
      color: "#834a11",
      border: "rgba(255, 152, 0, 0.3)",
    };
  }

  if (finalized === null || finalized === undefined) {
    return {
      label: "Pendente",
      background: "rgba(255, 193, 7, 0.15)",
      color: "#866c28",
      border: "rgba(255, 193, 7, 0.3)",
    };
  }

  if (customerArrived === 1) {
    return {
      label: "Cliente Chegou",
      background: "rgba(76, 175, 80, 0.15)",
      color: "#0a720f",
      border: "rgba(76, 175, 80, 0.3)",
    };
  }

  // Default: Confirmada
  return {
    label: "Confirmada",
    background: "rgba(33, 150, 243, 0.15)",
    color: "#1976d2",
    border: "rgba(33, 150, 243, 0.3)",
  };
};

export function ReservationSearch() {
  const navigate = useNavigate();
  const { pluginspace_id } = useParams();
  const { getToken } = usePluginspace();
  const [searchType, setSearchType] = useState<"phone" | "code">("phone");
  const [phoneValue, setPhoneValue] = useState("");
  const [codeValue, setCodeValue] = useState("");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [searched, setSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [bookingConfigId, setBookingConfigId] = useState<number | null>(null);
  const [bookingConfigData, setBookingConfigData] = useState<any>(null);

  const handleSearch = async () => {
    setIsSearching(true);
    setSearched(false);

    try {
      // Primeiro, buscar o bookingConfigId se ainda não temos
      if (!bookingConfigId) {
        const configResponse = await api.get(
          "/api/queueReservation/bookingConfig",
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          },
        );

        if (configResponse.data?.data?.[0]?.id) {
          const config = configResponse.data.data[0];
          setBookingConfigId(config.id);
          setBookingConfigData(config);
          await performSearch(config.id);
        }
      } else {
        await performSearch(bookingConfigId);
      }
    } catch (error) {
      console.error("Erro ao buscar:", error);
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: "Não foi possível realizar a busca. Tente novamente.",
      });
      setIsSearching(false);
    }
  };

  const performSearch = async (configId: number) => {
    const searchValue =
      searchType === "phone" ? phoneValue.replace(/\D/g, "") : codeValue;

    // Para busca por código, precisamos buscar todas as reservas e filtrar
    const endpoint =
      searchType === "code"
        ? `/api/queueReservation/booking/${configId}`
        : `/api/queueReservation/booking/${configId}/${searchValue}`;

    const searchDescription =
      searchType === "code" ? "código de reserva" : "número de telefone";

    try {
      const response = await api.get(endpoint, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      });

      const bookingResponse = response.data;

      if (bookingResponse?.data) {
        let bookingsData = Array.isArray(bookingResponse.data)
          ? bookingResponse.data
          : [bookingResponse.data];

        // Se estiver buscando por código, filtrar pelo ID
        if (searchType === "code") {
          bookingsData = bookingsData.filter(
            (booking: any) => booking.id === searchValue,
          );
        }

        // Filtrar apenas reservas com data atual ou futura
        bookingsData = bookingsData.filter((booking: any) =>
          isCurrentOrFutureDate(booking.date),
        );

        console.log(
          "Bookings recebidos (após filtro de datas):",
          JSON.stringify(bookingsData, null, 2),
        );
        if (bookingsData.length > 0) {
          console.log("Primeiro booking:", bookingsData[0]);
        }
        setBookings(bookingsData);
        setSearched(true);
      } else {
        setBookings([]);
        setSearched(true);
      }
    } catch (err: any) {
      let text = `Não foi possível encontrar reservas com esse ${searchDescription}.`;
      if (err?.response?.data?.message) {
        text = err.response.data.message;
      }

      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text,
      });

      setBookings([]);
      setSearched(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="reservation-search">
      <div
        style={{
          background:
            "linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRadius: "24px",
          padding: "32px",
          boxShadow:
            "0 8px 24px rgba(102, 126, 234, 0.15), 0 4px 12px rgba(102, 126, 234, 0.1)",
          border: "1px solid rgba(102, 126, 234, 0.25)",
          position: "relative" as const,
          overflow: "hidden",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          fontFamily: "'Poppins', sans-serif",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow =
            "0 12px 32px rgba(102, 126, 234, 0.2), 0 8px 16px rgba(102, 126, 234, 0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow =
            "0 8px 24px rgba(102, 126, 234, 0.15), 0 4px 12px rgba(102, 126, 234, 0.1)";
        }}
      >
        {/* Gradient overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
            pointerEvents: "none",
          }}
        ></div>

        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Search Type Tabs */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              padding: "8px",
              background: "rgba(255, 255, 255, 0.15)",
              borderRadius: "16px",
              marginBottom: "24px",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
          >
            <button
              onClick={() => setSearchType("phone")}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "12px 16px",
                border: "none",
                borderRadius: "12px",
                background:
                  searchType === "phone"
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "transparent",
                color: searchType === "phone" ? "white" : "#667eea",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: "600",
                fontSize: "15px",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow:
                  searchType === "phone"
                    ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                    : "none",
              }}
              onMouseEnter={(e) => {
                if (searchType !== "phone") {
                  e.currentTarget.style.background = "rgba(102, 126, 234, 0.1)";
                  e.currentTarget.style.color = "#667eea";
                }
              }}
              onMouseLeave={(e) => {
                if (searchType !== "phone") {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#667eea";
                }
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 5C3 3.89543 3.89543 3 5 3H8.27924C8.70967 3 9.09181 3.27543 9.22792 3.68377L10.7257 8.17721C10.8831 8.64932 10.6694 9.16531 10.2243 9.38787L7.96701 10.5165C9.06925 12.9612 11.0388 14.9308 13.4835 16.033L14.6121 13.7757C14.8347 13.3306 15.3507 13.1169 15.8228 13.2743L20.3162 14.7721C20.7246 14.9082 21 15.2903 21 15.7208V19C21 20.1046 20.1046 21 19 21H18C9.71573 21 3 14.2843 3 6V5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Telefone
            </button>
            <button
              onClick={() => setSearchType("code")}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "12px 16px",
                border: "none",
                borderRadius: "12px",
                background:
                  searchType === "code"
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "transparent",
                color: searchType === "code" ? "white" : "#667eea",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: "600",
                fontSize: "15px",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow:
                  searchType === "code"
                    ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                    : "none",
              }}
              onMouseEnter={(e) => {
                if (searchType !== "code") {
                  e.currentTarget.style.background = "rgba(102, 126, 234, 0.1)";
                  e.currentTarget.style.color = "#667eea";
                }
              }}
              onMouseLeave={(e) => {
                if (searchType !== "code") {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#667eea";
                }
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Código
            </button>
          </div>

          {/* Search Input */}
          <div style={{ marginBottom: "24px" }}>
            {searchType === "phone" ? (
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "16px",
                    display: "flex",
                    alignItems: "center",
                    color: "#667eea",
                    zIndex: 1,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 5C3 3.89543 3.89543 3 5 3H8.27924C8.70967 3 9.09181 3.27543 9.22792 3.68377L10.7257 8.17721C10.8831 8.64932 10.6694 9.16531 10.2243 9.38787L7.96701 10.5165C9.06925 12.9612 11.0388 14.9308 13.4835 16.033L14.6121 13.7757C14.8347 13.3306 15.3507 13.1169 15.8228 13.2743L20.3162 14.7721C20.7246 14.9082 21 15.2903 21 15.7208V19C21 20.1046 20.1046 21 19 21H18C9.71573 21 3 14.2843 3 6V5Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <InputMask
                  mask="(99) 99999-9999"
                  value={phoneValue}
                  onChange={(e) => setPhoneValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="(11) 98765-4321"
                  style={{
                    width: "100%",
                    padding: "16px 16px 16px 48px",
                    border: "2px solid rgba(102, 126, 234, 0.25)",
                    borderRadius: "16px",
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "16px",
                    color: "#4c51bf",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                  onFocus={(e) => {
                    e.target.style.outline = "none";
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow =
                      "0 0 0 4px rgba(102, 126, 234, 0.1)";
                    e.target.style.background = "rgba(255, 255, 255, 0.95)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(102, 126, 234, 0.25)";
                    e.target.style.boxShadow = "none";
                    e.target.style.background = "rgba(255, 255, 255, 0.8)";
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "16px",
                    display: "flex",
                    alignItems: "center",
                    color: "#667eea",
                    zIndex: 1,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M7 8H17M7 12H17M7 16H11M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  value={codeValue}
                  onChange={(e) => setCodeValue(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  placeholder="Digite o código da reserva"
                  style={{
                    width: "100%",
                    padding: "16px 16px 16px 48px",
                    border: "2px solid rgba(102, 126, 234, 0.25)",
                    borderRadius: "16px",
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "16px",
                    color: "#4c51bf",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                  onFocus={(e) => {
                    e.target.style.outline = "none";
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow =
                      "0 0 0 4px rgba(102, 126, 234, 0.1)";
                    e.target.style.background = "rgba(255, 255, 255, 0.95)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(102, 126, 234, 0.25)";
                    e.target.style.boxShadow = "none";
                    e.target.style.background = "rgba(255, 255, 255, 0.8)";
                  }}
                />
              </div>
            )}
          </div>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            disabled={
              isSearching ||
              (searchType === "phone" &&
                phoneValue.replace(/\D/g, "").length < 10) ||
              (searchType === "code" && !codeValue)
            }
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              padding: "16px",
              border: "none",
              borderRadius: "16px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: "600",
              fontSize: "16px",
              cursor:
                isSearching ||
                (searchType === "phone" &&
                  phoneValue.replace(/\D/g, "").length < 10) ||
                (searchType === "code" && !codeValue)
                  ? "not-allowed"
                  : "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
              opacity:
                isSearching ||
                (searchType === "phone" &&
                  phoneValue.replace(/\D/g, "").length < 10) ||
                (searchType === "code" && !codeValue)
                  ? 0.6
                  : 1,
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(102, 126, 234, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 16px rgba(102, 126, 234, 0.3)";
            }}
          >
            {isSearching ? (
              <>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ animation: "spin 1s linear infinite" }}
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeDasharray="32"
                    strokeLinecap="round"
                    opacity="0.3"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeDasharray="20 32"
                    strokeLinecap="round"
                  />
                </svg>
                Buscando...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="11"
                    cy="11"
                    r="8"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M21 21L16.65 16.65"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Buscar Reserva
              </>
            )}
          </button>

          {/* Help Text */}
          <p
            style={{
              marginTop: "16px",
              textAlign: "center",
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px",
              color: "#7e57c2",
              fontWeight: "500",
            }}
          >
            {searchType === "phone"
              ? "Digite o telefone cadastrado na reserva"
              : "Digite o código que você recebeu por e-mail ou WhatsApp"}
          </p>
        </div>
      </div>

      {/* Results */}
      {searched && (
        <div
          style={{
            marginTop: "32px",
            animation: "fadeIn 0.5s ease-in-out",
          }}
        >
          {bookings.length === 0 ? (
            <div
              style={{
                background:
                  "linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(251, 140, 0, 0.05) 100%)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                borderRadius: "20px",
                padding: "40px",
                textAlign: "center",
                border: "2px dashed rgba(255, 152, 0, 0.3)",
                position: "relative" as const,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <SearchX size={48} style={{ color: "#ff9800", opacity: 0.8 }} />
              </div>
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: "700",
                  fontSize: "18px",
                  color: "#e65100",
                  marginBottom: "8px",
                }}
              >
                Nenhuma reserva encontrada
              </p>
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: "500",
                  fontSize: "14px",
                  color: "#f57c00",
                }}
              >
                Verifique o {searchType === "code" ? "código" : "telefone"} e
                tente novamente
              </p>
            </div>
          ) : (
            <>
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: "600",
                  fontSize: "14px",
                  color: "#667eea",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Sparkles size={16} style={{ color: "#667eea" }} />
                {bookings.length} reserva{bookings.length > 1 ? "s" : ""}{" "}
                encontrada{bookings.length > 1 ? "s" : ""}
              </p>

              {bookings.map((booking) => (
                <div
                  key={booking.id}
                  onClick={() => {
                    if (!pluginspace_id) return;
                    const cleanPhone = (booking.phone || "").replace(/\D/g, "");
                    navigate(
                      `/${pluginspace_id}/booking/success/${cleanPhone}/${booking.id}`,
                    );
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow =
                      "0 12px 32px rgba(102, 126, 234, 0.22), 0 8px 16px rgba(102, 126, 234, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(102, 126, 234, 0.15), 0 4px 12px rgba(102, 126, 234, 0.1)";
                  }}
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    borderRadius: "20px",
                    padding: "24px",
                    marginBottom: "16px",
                    boxShadow:
                      "0 8px 24px rgba(102, 126, 234, 0.15), 0 4px 12px rgba(102, 126, 234, 0.1)",
                    border: "1px solid rgba(102, 126, 234, 0.25)",
                    position: "relative" as const,
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
                      pointerEvents: "none",
                    }}
                  ></div>

                  <div style={{ position: "relative", zIndex: 1 }}>
                    {/* Header com ID e Status */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "16px",
                        flexWrap: "wrap" as const,
                        gap: "12px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "18px",
                              fontWeight: "700",
                              color: "#667eea",
                              letterSpacing: "-0.3px",
                            }}
                          >
                            #{booking.id}
                          </span>
                          {(() => {
                            const statusInfo = getBookingStatusInfo(
                              booking.finalized as any,
                              booking.customerArrived,
                            );
                            return (
                              <span
                                style={{
                                  fontSize: "12px",
                                  padding: "4px 12px",
                                  borderRadius: "12px",
                                  fontWeight: "600",
                                  background: statusInfo.background,
                                  color: statusInfo.color,
                                  border: `1px solid ${statusInfo.border}`,
                                }}
                              >
                                {statusInfo.label}
                              </span>
                            );
                          })()}
                        </div>
                        <p
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "13px",
                            color: "#7e57c2",
                            fontWeight: "500",
                          }}
                        >
                          {booking.name} • {booking.email}
                        </p>
                      </div>
                    </div>

                    {/* Detalhes da Reserva */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(150px, 1fr))",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "12px",
                          borderRadius: "12px",
                          background: "rgba(255, 255, 255, 0.5)",
                        }}
                      >
                        <MapPin
                          size={16}
                          style={{ color: "#512da8", flexShrink: 0 }}
                        />
                        <span
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#512da8",
                          }}
                        >
                          {(() => {
                            const b = booking as any;

                            console.log("Debug ambiente:", {
                              bookingEnvironmentId: b.bookingEnvironmentId,
                              hasBookingConfigData: !!bookingConfigData,
                              hasEnvironments: !!bookingConfigData?.environment,
                              environmentsLength:
                                bookingConfigData?.environment?.length,
                              firstEnvironment:
                                bookingConfigData?.environment?.[0],
                            });

                            // Primeiro tenta usar os dados que já vêm na reserva
                            if (b.bookingEnvironment?.name)
                              return b.bookingEnvironment.name;
                            if (b.environment?.name) return b.environment.name;

                            // Se não vier, busca no bookingConfigData usando o bookingEnvironmentId
                            if (
                              b.bookingEnvironmentId &&
                              bookingConfigData?.environment
                            ) {
                              const envId = b.bookingEnvironmentId;
                              // Tenta comparar como string e como número
                              const env = bookingConfigData.environment.find(
                                (e: any) =>
                                  e.id == envId ||
                                  e.id === parseInt(envId) ||
                                  String(e.id) === String(envId),
                              );

                              console.log("Procurando ambiente:", {
                                searchingFor: envId,
                                found: env,
                                foundName: env?.name,
                                allEnvironments:
                                  bookingConfigData.environment.map(
                                    (e: any) => ({ id: e.id, name: e.name }),
                                  ),
                              });

                              if (env) {
                                console.log(
                                  "Retornando nome do ambiente:",
                                  env.name,
                                );
                                return env.name;
                              }
                            }

                            return "N/A";
                          })()}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "12px",
                          borderRadius: "12px",
                          background: "rgba(255, 255, 255, 0.5)",
                        }}
                      >
                        <CalendarDays
                          size={16}
                          style={{ color: "#512da8", flexShrink: 0 }}
                        />
                        <span
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#512da8",
                          }}
                        >
                          {formatDateBR(booking.date)}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "12px",
                          borderRadius: "12px",
                          background: "rgba(255, 255, 255, 0.5)",
                        }}
                      >
                        <Clock
                          size={16}
                          style={{ color: "#512da8", flexShrink: 0 }}
                        />
                        <span
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#512da8",
                          }}
                        >
                          {(() => {
                            const b = booking as any;
                            return (
                              b.bookingHour?.hour ||
                              b.hour?.hour ||
                              b.time ||
                              "N/A"
                            );
                          })()}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "12px",
                          borderRadius: "12px",
                          background: "rgba(255, 255, 255, 0.5)",
                        }}
                      >
                        <Users
                          size={16}
                          style={{ color: "#512da8", flexShrink: 0 }}
                        />
                        <span
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#512da8",
                          }}
                        >
                          {booking.people} pessoa{booking.people > 1 ? "s" : ""}
                        </span>
                      </div>

                      {/* Necessidades de Acessibilidade - Mostrar apenas se existir */}
                      {(() => {
                        const b = booking as any;

                        console.log("Debug acessibilidade:", {
                          bookingId: b.id,
                          accessibilityNeeds: b.accessibilityNeeds,
                          accessibilityNeeds_type: typeof b.accessibilityNeeds,
                          allKeys: Object.keys(b),
                        });

                        const accessibilityNeeds = b.accessibilityNeeds;

                        // Verifica se existe e não é uma string vazia
                        if (
                          !accessibilityNeeds ||
                          accessibilityNeeds === "" ||
                          accessibilityNeeds === "null"
                        ) {
                          console.log(
                            "Acessibilidade não encontrada ou vazia para booking",
                            b.id,
                          );
                          return null;
                        }

                        console.log(
                          "Mostrando acessibilidade:",
                          accessibilityNeeds,
                        );

                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "12px",
                              borderRadius: "12px",
                              background: "rgba(103, 58, 183, 0.1)",
                              border: "1px solid rgba(103, 58, 183, 0.25)",
                            }}
                          >
                            <Accessibility
                              size={16}
                              style={{ color: "#673ab7", flexShrink: 0 }}
                            />
                            <span
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#673ab7",
                              }}
                            >
                              {accessibilityNeeds}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
